import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Building2, User, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

const EMPTY = {
  company_name: "", authorized_name: "", authorized_title: "", authorized_phone: "",
  authorized_email: "", tax_office: "", tax_number: "", phone: "", email: "", address: "", notes: "",
};

export default function Customers() {
  const { can } = useAuth();
  const canDeleteCustomer = can("customer_delete");
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(0);

  const load = useCallback(async (query = "", pageNum = 0) => {
    setLoading(true);
    try {
      const res = await api.get("/customers", { params: { q: query, limit: PAGE_SIZE, skip: pageNum * PAGE_SIZE } });
      setItems(res.data);
      setTotal(Number(res.headers["x-total-count"]) || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(q, 0); }, 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = items;
  const goPage = (p) => { setPage(p); load(q, p); };

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.company_name && !form.authorized_name) {
      toast.error("Firma adı veya yetkili adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      delete payload.created_by; delete payload.created_by_name;
      if (editId) await api.put(`/customers/${editId}`, payload);
      else await api.post("/customers", payload);
      toast.success(editId ? "Müşteri güncellendi" : "Müşteri eklendi");
      setOpen(false);
      load(q, page);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Müşteri silindi");
      load(q, page);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <PageHeader
        title="Müşteriler"
        subtitle={`Müşteri firma ve yetkili bilgilerini yönetin · Toplam ${total} müşteri`}
        action={
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={openNew} data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-2" /> Yeni Müşteri
          </Button>
        }
      />

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Firma, yetkili, vergi no ara..." value={q} onChange={(e) => setQ(e.target.value)}
          className="pl-9" data-testid="customer-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Building2 className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-slate-500">Müşteri bulunamadı.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {pageItems.map((c) => (
            <Card key={c.id} className="p-5 border-slate-200 hover:shadow-md transition-all duration-200" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 cursor-pointer flex-1" onClick={() => navigate(`/musteriler/${c.id}`)}>
                  <p className="font-heading font-bold text-base truncate">{c.company_name || c.authorized_name}</p>
                  {c.authorized_name && c.company_name && (
                    <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                      <User className="w-3.5 h-3.5" /> {c.authorized_name}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {canDeleteCustomer && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" data-testid={`delete-customer-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Müşteriyi sil?</AlertDialogTitle>
                        <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => remove(c.id)}>Sil</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-gray-600">
                {c.phone && <p>{c.phone}</p>}
                {c.email && <p className="truncate">{c.email}</p>}
                {c.tax_number && <p className="text-xs text-slate-400">VN: {c.tax_number} · {c.tax_office}</p>}
              </div>
              <p className="text-xs text-slate-400 mt-3">Ekleyen: {c.created_by_name || "-"}</p>
            </Card>
          ))}
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">Sayfa {page + 1}/{totalPages} · {total} müşteri</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" disabled={page === 0} onClick={() => goPage(Math.max(0, page - 1))} data-testid="customer-prev"><ChevronLeft className="w-4 h-4 mr-1" />Önceki</Button>
            <Button variant="outline" size="sm" className="rounded-xl" disabled={page + 1 >= totalPages} onClick={() => goPage(page + 1)} data-testid="customer-next">Sonraki<ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2 space-y-2">
              <Label>Firma Adı</Label>
              <Input value={form.company_name} onChange={set("company_name")} data-testid="cf-company" />
            </div>
            <div className="space-y-2">
              <Label>Vergi Dairesi</Label>
              <Input value={form.tax_office} onChange={set("tax_office")} data-testid="cf-taxoffice" />
            </div>
            <div className="space-y-2">
              <Label>Vergi Numarası</Label>
              <Input value={form.tax_number} onChange={set("tax_number")} data-testid="cf-taxno" />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="cf-phone" />
            </div>
            <div className="space-y-2">
              <Label>E-posta</Label>
              <Input value={form.email} onChange={set("email")} data-testid="cf-email" />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Adres</Label>
              <Textarea value={form.address} onChange={set("address")} rows={2} data-testid="cf-address" />
            </div>
            <div className="sm:col-span-2 pt-2 border-t border-slate-100">
              <p className="text-xs font-bold uppercase tracking-widest text-rose-600">Yetkili Bilgileri</p>
            </div>
            <div className="space-y-2">
              <Label>Yetkili Adı</Label>
              <Input value={form.authorized_name} onChange={set("authorized_name")} data-testid="cf-authname" />
            </div>
            <div className="space-y-2">
              <Label>Ünvan</Label>
              <Input value={form.authorized_title} onChange={set("authorized_title")} data-testid="cf-authtitle" />
            </div>
            <div className="space-y-2">
              <Label>Yetkili Telefon</Label>
              <Input value={form.authorized_phone} onChange={set("authorized_phone")} data-testid="cf-authphone" />
            </div>
            <div className="space-y-2">
              <Label>Yetkili E-posta</Label>
              <Input value={form.authorized_email} onChange={set("authorized_email")} data-testid="cf-authemail" />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Notlar</Label>
              <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="cf-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={save} disabled={saving} data-testid="save-customer-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
